/**
 * Pure calendar helpers for /orders/school (month + week views).
 *
 * No React, no `@/api` runtime imports — the components and the unit
 * tests both import these directly. All date keys are `YYYY-MM-DD` and
 * UTC-anchored so the day boundary never shifts under the viewer's
 * timezone for a calendar that's already date-only. The one exception is
 * `todayKey`, which reads the viewer's LOCAL wall clock — see its docblock.
 *
 * The grid is WEEKDAY-ONLY (Mon–Fri, no weekends) per the design.
 */

/** `YYYY-MM-DD` for a (year, 1-based month, day). */
export function dateKey(year: number, month: number, day: number): string {
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

/** `YYYY-MM-DD` for a Date's UTC wall clock. */
export function dateKeyFromDate(date: Date): string {
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
}

/**
 * `YYYY-MM-DD` for the viewer's LOCAL calendar day. Date keys stay
 * UTC-anchored labels, but "today" must follow the local wall clock — a UTC
 * "today" flips to tomorrow mid-evening US time, shifting the today ring and
 * every week anchored on it. Ordering authority stays server-side
 * (`computeOrderDeadline`); this key is display/anchoring only.
 */
export function todayKey(now: Date = new Date()): string {
  return dateKey(now.getFullYear(), now.getMonth() + 1, now.getDate())
}

/** Parse a `YYYY-MM-DD` key to a noon-UTC Date (stable, DST-proof). */
export function dateFromKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12))
}

/** "Aug 20" — locale short month + day-of-month for a day key (UTC-stable). */
export function monthDayLabel(key: string): string {
  return dateFromKey(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** Mon=1 … Fri=5, Sat=6, Sun=0 (UTC). */
function utcWeekday(key: string): number {
  return dateFromKey(key).getUTCDay()
}

/** True for Mon–Fri. */
export function isWeekday(key: string): boolean {
  const wd = utcWeekday(key)
  return wd >= 1 && wd <= 5
}

/**
 * Weekday-only month grid: an array of weeks, each a 5-slot Mon–Fri row.
 * Slots before the 1st (or after the last day) of the month are `null`
 * pad cells. Weekends are never represented.
 *
 * e.g. a month whose 1st falls on a Wednesday →
 * first week = [null, null, '…-01', '…-02', '…-03'].
 */
export function buildWeekdayMonth(year: number, month: number): Array<Array<string | null>> {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const weeks: Array<Array<string | null>> = []
  let week: Array<string | null> = []

  for (let day = 1; day <= lastDay; day++) {
    const key = dateKey(year, month, day)
    const wd = utcWeekday(key)
    if (wd === 0 || wd === 6) {
      continue // skip weekends
    }
    if (week.length === 0 && wd > 1) {
      // Pad leading slots (Mon=1): Tue→1 pad, Wed→2 pads, etc.
      for (let i = 1; i < wd; i++) {
        week.push(null)
      }
    }
    week.push(key)
    if (wd === 5) {
      weeks.push(week)
      week = []
    }
  }
  if (week.length > 0) {
    while (week.length < 5) {
      week.push(null)
    }
    weeks.push(week)
  }
  return weeks
}

/**
 * The Mon–Fri date keys for the week containing `key`. Always 5 entries,
 * even when some fall in an adjacent month (the week view shows the real
 * week, not a month-clipped one). Weekend keys snap FORWARD to the upcoming
 * Mon–Fri block — on a weekend, "this week" means the service week being
 * planned, not the one that just ended.
 */
export function weekOf(key: string): string[] {
  const date = dateFromKey(key)
  const wd = date.getUTCDay() // 0=Sun … 6=Sat
  const deltaToMonday = wd === 0 ? 1 : wd === 6 ? 2 : 1 - wd
  const monday = new Date(date)
  monday.setUTCDate(date.getUTCDate() + deltaToMonday)
  const out: string[] = []
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday)
    d.setUTCDate(monday.getUTCDate() + i)
    out.push(dateKeyFromDate(d))
  }
  return out
}

/** The Monday (date key) of the week containing `key`. */
export function mondayOf(key: string): string {
  return weekOf(key)[0]!
}

/**
 * Whether a day is still orderable: it must be in the future AND its week's
 * order window must still be open. A future day whose week deadline has
 * already passed (e.g. a later weekday in the current, now-closed week) is
 * NOT orderable.
 */
export function isDayOrderable(dayKey: string, today: string, weekClosed: boolean): boolean {
  return dayKey > today && !weekClosed
}

/** Shift a date key by `days` (returns the new key). */
export function addDaysKey(key: string, days: number): string {
  const d = dateFromKey(key)
  d.setUTCDate(d.getUTCDate() + days)
  return dateKeyFromDate(d)
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Relative week label for the week-nav: "This week" / "Next week" /
 * "Last week", else "Week of Jun 9". Compares the Mondays of `anchorKey`
 * and `today`.
 */
export function weekNavLabel(anchorKey: string, today: string): string {
  const anchorMonday = dateFromKey(mondayOf(anchorKey))
  const todayMonday = dateFromKey(mondayOf(today))
  const diff = Math.round((anchorMonday.getTime() - todayMonday.getTime()) / WEEK_MS)
  if (diff === 0) {
    return 'This week'
  }
  if (diff === 1) {
    return 'Next week'
  }
  if (diff === -1) {
    return 'Last week'
  }
  const month = anchorMonday.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
  return `Week of ${month} ${anchorMonday.getUTCDate()}`
}

/** Human label for a week, e.g. "Aug 11 – 15, 2025" / "Jun 30 – Jul 4, 2025". */
export function weekLabel(weekKeys: string[]): string {
  const first = weekKeys[0]
  const last = weekKeys[weekKeys.length - 1]
  if (!first || !last) {
    return ''
  }
  const a = dateFromKey(first)
  const b = dateFromKey(last)
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
  const year = b.getUTCFullYear()
  if (a.getUTCMonth() === b.getUTCMonth()) {
    return `${month(a)} ${a.getUTCDate()} – ${b.getUTCDate()}, ${year}`
  }
  return `${month(a)} ${a.getUTCDate()} – ${month(b)} ${b.getUTCDate()}, ${year}`
}

// ---------------------------------------------------------------------------
// Coverage + meal counts derived from submitted batches.
// ---------------------------------------------------------------------------

/**
 * Sum of item quantities across a day's batches — the "N meals" count.
 * System-milk lines (derived 1:1 from the meal counts) are excluded, or
 * every placed day would read back doubled; absent flags (older cached
 * payloads) count as meals.
 */
export function mealsForDay(
  batches: ReadonlyArray<{ items: ReadonlyArray<{ quantity: number; systemMilk?: boolean }> }>,
): number {
  return batches.reduce((sum, b) => sum + b.items.reduce((s, i) => s + (i.systemMilk ? 0 : i.quantity), 0), 0)
}
